import { useCallback, useRef } from 'react';

type SoundType = 'select' | 'success' | 'failure' | 'alert' | 'activate' | 'countdown' | 'win';

export const useSound = () => {
  const audioContextRef = useRef<AudioContext | null>(null);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  const playSound = useCallback((type: SoundType, volume: number = 0.3) => {
    try {
      const ctx = getAudioContext();
      const now = ctx.currentTime;
      
      const gainNode = ctx.createGain();
      gainNode.connect(ctx.destination);
      gainNode.gain.value = volume;

      switch (type) {
        case 'select': {
          // Quick ascending pop (440Hz → 880Hz, 50ms)
          const osc = ctx.createOscillator();
          osc.type = 'square';
          osc.frequency.setValueAtTime(440, now);
          osc.frequency.exponentialRampToValueAtTime(880, now + 0.05);
          osc.connect(gainNode);
          osc.start(now);
          osc.stop(now + 0.05);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.05);
          break;
        }

        case 'success': {
          // Upward positive chime (523Hz → 784Hz, 200ms)
          const osc = ctx.createOscillator();
          osc.type = 'triangle';
          osc.frequency.setValueAtTime(523, now);
          osc.frequency.exponentialRampToValueAtTime(784, now + 0.2);
          osc.connect(gainNode);
          osc.start(now);
          osc.stop(now + 0.2);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
          break;
        }

        case 'failure': {
          // Descending buzzer (200Hz → 100Hz, 300ms)
          const osc = ctx.createOscillator();
          osc.type = 'sawtooth';
          osc.frequency.setValueAtTime(200, now);
          osc.frequency.exponentialRampToValueAtTime(100, now + 0.3);
          osc.connect(gainNode);
          osc.start(now);
          osc.stop(now + 0.3);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
          break;
        }

        case 'alert': {
          // Attention-grabbing alarm (oscillating, 500ms)
          const osc = ctx.createOscillator();
          osc.type = 'square';
          osc.frequency.setValueAtTime(440, now);
          for (let i = 0; i < 5; i++) {
            osc.frequency.setValueAtTime(440, now + i * 0.1);
            osc.frequency.setValueAtTime(880, now + i * 0.1 + 0.05);
          }
          osc.connect(gainNode);
          osc.start(now);
          osc.stop(now + 0.5);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
          break;
        }

        case 'activate': {
          // Confirmation beep (880Hz, 100ms)
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = 880;
          osc.connect(gainNode);
          osc.start(now);
          osc.stop(now + 0.1);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
          break;
        }

        case 'countdown': {
          // Sharp tick (440Hz, 50ms)
          const osc = ctx.createOscillator();
          osc.type = 'square';
          osc.frequency.value = 440;
          osc.connect(gainNode);
          osc.start(now);
          osc.stop(now + 0.05);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.05);
          break;
        }

        case 'win': {
          // Victory fanfare (arpeggio, 1000ms)
          const frequencies = [523, 659, 784, 1047];
          frequencies.forEach((freq, i) => {
            const osc = ctx.createOscillator();
            osc.type = 'sine';
            osc.frequency.value = freq;
            const oscGain = ctx.createGain();
            oscGain.gain.value = 0.2;
            osc.connect(oscGain);
            oscGain.connect(gainNode);
            osc.start(now + i * 0.2);
            osc.stop(now + i * 0.2 + 0.4);
            oscGain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.2 + 0.4);
          });
          break;
        }
      }
    } catch (error) {
      // Fail silently if audio is unavailable
      console.debug('Sound playback failed:', error);
    }
  }, [getAudioContext]);

  return { playSound };
};
